import { useContext, useEffect, useState, useMemo } from "react";
import api from "../api/api";
import { AuthContext } from "../context/AuthContext";
import { useParams } from "react-router-dom";

export default function FighterProfile() {
  const { id } = useParams();
  const { user, setAuth } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";
  const [formData, setFormData] = useState({});
  const [newImage, setNewImage] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [profileLoaded, setProfileLoaded] = useState(false);

  const normalizeFighter = (data) => {
  // If data has a .fighter property, use that. Otherwise, use the data itself.
  const base = data?.fighter || data || {};
  return base;
};

  useEffect(() => {
    if (!user) {
      const storedUser = localStorage.getItem("userInfo");
      const token = localStorage.getItem("userToken");
      if (storedUser && token) setAuth(token, JSON.parse(storedUser));
    }
  }, [user, setAuth]);

  useEffect(() => {
    if (!user) return;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        
        // LOGIC: If there's an ID in the URL and user is Admin, fetch THAT fighter.
        // Otherwise, fetch the logged-in user's own profile.
        let endpoint;
        if (id && user.role === 'admin') {
          endpoint = `/fighters/${id}`; 
        } else {
          endpoint = user.role === "fighter" ? "/fighters/profile" : "/profile";
        }

        const res = await api.get(endpoint);
        setFormData(normalizeFighter(res));

        const clubRes = await api.get("/clubs");
        setClubs(Array.isArray(clubRes) ? clubRes : []);
      } catch (err) {
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user, id]);

const handleResetPassword = async () => {
  const newPass = prompt("Enter new temporary password:");
  if (!newPass) return;

  try {
    // Make sure 'api' is the axios instance you imported
    // And use the correct path without the 'admin' prefix
    const res = await api.put(`/fighters/${id}/reset-password`, { password: newPass });
    
    alert("Password updated successfully!");
  } catch (err) {
    console.error(err);
    const errorMsg = err.response?.data?.error || err.message;
    alert(`Failed: ${errorMsg}`);
  }
};

  const age = useMemo(() => {
    if (!formData?.dob) return "N/A";
    const dob = new Date(formData.dob);
    const today = new Date();
    let a = today.getFullYear() - dob.getFullYear();
    if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) a--;
    return a > 0 ? a : 0;
  }, [formData?.dob]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const val = type === "number" || name === "club_id" ? (value === "" ? null : Number(value)) : value;
    setFormData({ ...formData, [name]: val });
  };

  const handleFileChange = (e) => setNewImage(e.target.files[0]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  setMessage("");
  setError("");

  try {
    const data = new FormData();
    
    // Determine the endpoint: 
    // If there's an ID and user is admin, we are editing a specific fighter.
    const isAdminEditing = id && user?.role === "admin";
    const endpoint = isAdminEditing ? `/fighters/${id}` : "/fighters/profile";

    Object.entries(formData).forEach(([key, value]) => {
  const isAdminEditing = id && user?.role === "admin";
  
  // 1. Define fields that regular fighters can NEVER change
  const protectedFields = ["id", "fighter_id", "user_id", "coach_name"];

  if (!isAdminEditing) {
    // 2. If NOT an admin, add email, dob, and gender to the restricted list
    protectedFields.push("email", "dob", "gender");
  }

  if (protectedFields.includes(key)) return;
  if (value !== undefined && value !== null) data.append(key, value);
});

    if (newImage) data.append("image", newImage);

    const res = await api.putForm(endpoint, data);
    const updated = normalizeFighter(res);

    setFormData(updated);
    setNewImage(null);
    setMessage("Profile updated successfully!");

    // Only update the Global Auth Context if the user is editing THEMSELVES
    if (!id) {
      setAuth(localStorage.getItem("userToken"), { ...user, ...updated });
    }
  } catch (err) {
    setError(err?.response?.data?.error || err?.message || "Profile update failed");
    console.error(err);
  }
};

  if (loading) return <div className="text-white text-center py-10 text-xl">Loading profile...</div>;

  const imageSource = newImage
  ? URL.createObjectURL(newImage)
  : formData?.image_large // Check the high-res version first
    ? `https://warfc.com/api${formData.image_large}`
    : formData?.image_url
    ? `https://warfc.com/api${formData.image_url}`
    : `https://ui-avatars.com/api/?name=${formData?.name || "Fighter"}`;
  const fighterClub = clubs.find((c) => c.club_id === formData.club_id);

  return (
    <div className="max-w-5xl mx-auto mt-10 p-6 rounded-xl bg-gray-900 shadow-2xl border border-yellow-400">
      <h2 className="text-4xl font-extrabold text-yellow-400 mb-8 text-center">
  {id && user?.role === 'admin' ? `Editing: ${formData.name || 'Fighter'}` : 'My Fighter Profile'}
</h2>
      {message && <div className="bg-green-600 p-3 rounded mb-4 text-white">{message}</div>}
      {error && <div className="bg-red-600 p-3 rounded mb-4 text-white">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Profile Image & Club */}
<div className="flex flex-col md:flex-row gap-10">
  <div className="flex flex-col items-center md:w-1/3">
    <img src={imageSource} alt={formData?.name} className="w-40 h-40 rounded-full border-4 border-yellow-400 object-cover" />
    <input type="file" onChange={handleFileChange} className="mt-4 text-sm text-gray-300" />

    {/* ADDED CURLY BRACES HERE */}
    {(user?.role === "fighter" || (id && user?.role === "admin")) ? (
      <div className="mt-4 w-full">
        <label className="block text-sm font-semibold text-yellow-400 mb-1">Club</label>
        <select
          name="club_id"
          value={formData.club_id || ""}
          onChange={handleChange}
          className="w-full bg-gray-800 text-white border border-gray-600 rounded px-3 py-2"
        >
          <option value="">-- Select Club --</option>
          {clubs.map((club) => (
            <option key={club.club_id} value={club.club_id}>
              {club.club_name}
            </option>
          ))}
        </select>
      </div>
    ) : (
      fighterClub && (
        <div className="mt-4 flex items-center gap-2">
          <img
            src={`https://warfc.com/api/uploads/clubs/${fighterClub.logo_url || "default-club.png"}`}
            alt={fighterClub.club_name}
            className="w-10 h-10 rounded-full border border-yellow-400 object-cover"
          />
          <span className="text-yellow-400 font-semibold">{fighterClub.club_name}</span>
        </div>
      )
    )}
  </div>

          {/* Personal Info */}
          <div className="md:w-2/3 space-y-6 text-white">
            <div className="space-y-2">
              <label className="font-semibold">First Name</label>
              <input name="name" value={formData.name || ""} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
            </div>

            <div className="space-y-2">
              <label className="font-semibold">Surname</label>
              <input name="surname" value={formData.surname || ""} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
            </div>

            <div className="space-y-2">
  <label className="font-semibold text-white">Email</label>
  <input 
    name="email" // Ensure name attribute is present
    type="email"
    value={formData.email || ""} 
    onChange={handleChange}
    readOnly={!(id && user?.role === 'admin')} 
    disabled={!(id && user?.role === 'admin')} 
    className={`input-field w-full px-3 py-2 rounded border border-gray-600 ${
      (id && user?.role === 'admin') ? "bg-gray-800 text-white" : "bg-gray-700 text-gray-300"
    }`} 
  />
  {id && user?.role === 'admin' && (
    <p className="text-xs text-yellow-500 mt-1">
      Warning: Changing this will change the fighter's login username.
    </p>
  )}
</div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
  <label className="font-semibold">Date of Birth</label>
  <input 
    name="dob"
    type={id && user?.role === 'admin' ? "date" : "text"}
    value={id && user?.role === 'admin' ? (formData.dob ? formData.dob.split('T')[0] : "") : (formData?.dob ? new Date(formData.dob).toLocaleDateString() : "N/A")} 
    onChange={handleChange}
    readOnly={!id || user?.role !== 'admin'} 
    disabled={!id || user?.role !== 'admin'}
    className={`input-field w-full px-3 py-2 rounded border border-gray-600 ${
      (id && user?.role === 'admin') ? "bg-gray-800 text-white" : "bg-gray-700 text-gray-300"
    }`} 
  />
</div>
            </div>

            {/* Example for Gender */}
<div className="space-y-2">
  <label className="font-semibold">Gender</label>
  <input 
    name="gender"
    value={formData.gender?.toUpperCase() || ""} 
    onChange={handleChange}
    readOnly={user?.role !== 'admin'} 
    disabled={user?.role !== 'admin'}
    className={`input-field w-full px-3 py-2 rounded border border-gray-600 ${
      user?.role === 'admin' ? "bg-gray-800 text-white" : "bg-gray-700 text-gray-300"
    }`} 
  />
</div>
            <div className="space-y-2">
              <label className="font-semibold">Phone Number</label>
              <input name="phone_number" value={formData.phone_number || ""} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-yellow-400">Stats</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <label className="font-semibold">Height (cm)</label>
              <input name="height" type="number" value={formData.height || ""} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
            </div>
            <div className="space-y-1">
              <label className="font-semibold">Weight (kg)</label>
              <input name="actual_weight" type="number" value={formData.actual_weight || ""} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
            </div>
            <div className="space-y-1">
              <label className="font-semibold">Wins</label>
              <input name="wins" type="number" value={formData.wins || 0} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
            </div>
            <div className="space-y-1">
              <label className="font-semibold">Losses</label>
              <input name="losses" type="number" value={formData.losses || 0} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
            </div>
          </div>
        </div>

        {/* Coach */}
        <div className="space-y-2">
          <label className="font-semibold">Head Coach</label>
          <input value={fighterClub?.trainer_name ? `${fighterClub.trainer_name} (${fighterClub.club_name})` : "No club assigned"} readOnly disabled className="input-field w-full bg-gray-700 text-gray-300 px-3 py-2 rounded border border-gray-600" />
        </div>

        {/* Fighting Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="font-semibold">Fighting Style</label>
            <input name="fighting_style" value={formData.fighting_style || ""} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
          </div>
          <div className="space-y-2">
            <label className="font-semibold">Nickname</label>
            <input name="nickname" value={formData.nickname || ""} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
          </div>
        </div>

        {/* Nationality */}
        <div className="space-y-2">
          <label className="font-semibold">Nationality</label>
          <input name="nationality" value={formData.nationality || "Mongolia"} onChange={handleChange} className="input-field w-full placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
        </div>

        {/* Biography */}
        <div className="space-y-2">
          <label className="font-semibold">Biography</label>
          <textarea name="bio" value={formData.bio || ""} onChange={handleChange} className="input-field w-full h-32 placeholder-gray-400 text-white bg-gray-800 border border-gray-600 rounded px-3 py-2" />
        </div>
{isAdmin && (
  <button 
    type="button"
    onClick={handleResetPassword}
    className="mt-4 w-full border border-red-500 text-red-500 py-2 rounded-xl hover:bg-red-500 hover:text-white transition"
  >
    Force Reset Password
  </button>
)}
        <button type="submit" className="w-full bg-yellow-400 text-black font-bold py-3 rounded-xl hover:bg-yellow-300 transition text-lg">
          Save Profile
        </button>
      </form>
    </div>
  );
}
